import type {
  AdminTournamentBracket,
  AdminTournamentBracketMatch,
  AdminTournamentCategoryMatchSettings,
  AdminTournamentMatchSetScore,
} from "../services/eventRegistrationAdminService";
import type { MatchFormat, ScoreSetDraft } from "./tournamentOperationTypes";

export function visibleSetGames(set: AdminTournamentMatchSetScore) {
  const gamesA =
    set.gamesA !== null && set.gamesA !== undefined ? Number(set.gamesA) : null;
  const gamesB =
    set.gamesB !== null && set.gamesB !== undefined ? Number(set.gamesB) : null;
  const tieBreakA =
    set.tieBreakA !== null && set.tieBreakA !== undefined
      ? Number(set.tieBreakA)
      : null;
  const tieBreakB =
    set.tieBreakB !== null && set.tieBreakB !== undefined
      ? Number(set.tieBreakB)
      : null;
  const hasTieBreakDecision =
    tieBreakA !== null && tieBreakB !== null && tieBreakA !== tieBreakB;

  if (set.isMatchTieBreak) {
    if (gamesA !== null && gamesB !== null && gamesA !== gamesB) {
      return { gamesA, gamesB };
    }
    return {
      gamesA: hasTieBreakDecision && tieBreakA > tieBreakB ? 1 : 0,
      gamesB: hasTieBreakDecision && tieBreakB > tieBreakA ? 1 : 0,
    };
  }

  if (gamesA === 0 && gamesB === 0 && hasTieBreakDecision) {
    return { gamesA: tieBreakA, gamesB: tieBreakB };
  }
  return {
    gamesA: gamesA ?? 0,
    gamesB: gamesB ?? 0,
  };
}

export function formatSets(match: AdminTournamentBracketMatch) {
  if (!Array.isArray(match.sets) || match.sets.length === 0) return "x";
  return match.sets
    .map((set) => {
      const { gamesA, gamesB } = visibleSetGames(set);
      return `${gamesA}-${gamesB}`;
    })
    .join("  ");
}

export function inferBestOfSets(format?: string | null) {
  if (format === "bo5") return 5;
  if (format === "bo3") return 3;
  return 1;
}

export function matchFormatDefaults(format?: string | null) {
  if (format === "bo5") {
    return { bestOfSets: 5, gamesPerSet: 6, tieBreakAt: 6 };
  }
  if (format === "bo3") {
    return { bestOfSets: 3, gamesPerSet: 6, tieBreakAt: 6 };
  }
  if (format === "single_set_6") {
    return { bestOfSets: 1, gamesPerSet: 6, tieBreakAt: 6 };
  }
  return { bestOfSets: 1, gamesPerSet: 9, tieBreakAt: 8 };
}

export function isFinalRoundCode(roundCode?: string | null) {
  const normalized = String(roundCode || "")
    .trim()
    .toLowerCase();
  return normalized === "final" || normalized === "round_of_2";
}

export function isGroupStageMatch(match: AdminTournamentBracketMatch) {
  const stage = String(match.stage || "")
    .trim()
    .toLowerCase();
  const roundCode = String(match.roundCode || "")
    .trim()
    .toLowerCase();
  return stage === "group" || roundCode === "group" || roundCode === "group_stage";
}

export function createSettingsDraft(
  settings?: AdminTournamentCategoryMatchSettings | null,
) {
  const format = String(settings?.matchFormat || "single_set_9") as MatchFormat;
  const finalFormat = String(
    settings?.finalMatchFormat || format,
  ) as MatchFormat;
  const defaults = matchFormatDefaults(format);
  const finalDefaults = matchFormatDefaults(finalFormat);
  return {
    matchFormat: format,
    bestOfSets: String(settings?.bestOfSets || defaults.bestOfSets),
    gamesPerSet: String(settings?.gamesPerSet || defaults.gamesPerSet),
    tieBreakEnabled: settings?.tieBreakEnabled !== false,
    tieBreakAt: String(settings?.tieBreakAt || defaults.tieBreakAt),
    tieBreakPoints: String(settings?.tieBreakPoints || 7),
    matchTieBreakPoints: String(settings?.matchTieBreakPoints || 12),
    finalMatchFormat: finalFormat,
    finalBestOfSets: String(
      settings?.finalBestOfSets || finalDefaults.bestOfSets,
    ),
    finalGamesPerSet: String(
      settings?.finalGamesPerSet || finalDefaults.gamesPerSet,
    ),
    finalTieBreakEnabled: settings?.finalTieBreakEnabled !== false,
    finalTieBreakAt: String(
      settings?.finalTieBreakAt || finalDefaults.tieBreakAt,
    ),
    finalTieBreakPoints: String(settings?.finalTieBreakPoints || 7),
    finalMatchTieBreakPoints: String(settings?.finalMatchTieBreakPoints || 12),
  };
}

export type SettingsDraft = ReturnType<typeof createSettingsDraft>;

export function settingsPayloadFromDraft(draft: SettingsDraft) {
  return {
    matchFormat: draft.matchFormat,
    bestOfSets: Number(draft.bestOfSets || inferBestOfSets(draft.matchFormat)),
    gamesPerSet: Number(draft.gamesPerSet || 6),
    tieBreakEnabled: draft.tieBreakEnabled,
    tieBreakAt: Number(draft.tieBreakAt || 6),
    tieBreakPoints: Number(draft.tieBreakPoints || 7),
    matchTieBreakPoints: Number(draft.matchTieBreakPoints || 12),
    finalMatchFormat: draft.finalMatchFormat,
    finalBestOfSets: Number(
      draft.finalBestOfSets || inferBestOfSets(draft.finalMatchFormat),
    ),
    finalGamesPerSet: Number(draft.finalGamesPerSet || 6),
    finalTieBreakEnabled: draft.finalTieBreakEnabled,
    finalTieBreakAt: Number(draft.finalTieBreakAt || 6),
    finalTieBreakPoints: Number(draft.finalTieBreakPoints || 7),
    finalMatchTieBreakPoints: Number(draft.finalMatchTieBreakPoints || 12),
  };
}

export function scoreDraftForMatch(
  match: AdminTournamentBracketMatch,
  _bestOfSets = 1,
): ScoreSetDraft[] {
  const setCount = Math.max(1, Math.min(5, match.sets?.length || 1));
  return Array.from({ length: setCount }, (_item, index) => {
    const existing = match.sets?.[index];
    const visibleGames = existing ? visibleSetGames(existing) : null;
    return {
      gamesA: visibleGames ? String(visibleGames.gamesA) : "",
      gamesB: visibleGames ? String(visibleGames.gamesB) : "",
      tieBreakA:
        existing?.tieBreakA !== undefined && existing?.tieBreakA !== null
          ? String(existing.tieBreakA)
          : "",
      tieBreakB:
        existing?.tieBreakB !== undefined && existing?.tieBreakB !== null
          ? String(existing.tieBreakB)
          : "",
      isMatchTieBreak: Boolean(existing?.isMatchTieBreak),
    };
  });
}

export function findMatchInBracket(
  bracket: AdminTournamentBracket | null,
  matchId: number,
) {
  if (!bracket || !Number.isFinite(matchId) || matchId <= 0) return null;
  for (const round of bracket.rounds || []) {
    const match = round.matches.find((item) => Number(item.id) === matchId);
    if (match) return match;
  }
  return null;
}
